import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const MatchModal = ({ isOpen, onClose, currentUser, matchedUser }) => {
  const navigate = useNavigate();

  // Pre-generate random positions for confetti to avoid impure function during render
  const confettiPositions = useMemo(() => 
    [...Array(20)].map((_, i) => ({
      left: `${(i * 17 + 10) % 100}%`,
      top: `${(i * 23 + 5) % 100}%`,
      color: ['#FAB710', '#FFD700', '#FAB710', '#4ECDC4', '#fff'][i % 5],
    })), []
  );

  if (!isOpen || !matchedUser) return null;

  const handleSendMessage = () => {
    onClose();
    navigate("/matches");
  };

  const handleKeepSwiping = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleKeepSwiping}
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative w-full max-w-sm"
          >
            {/* Close button */}
            <button
              onClick={handleKeepSwiping}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Card */}
            <div className="bg-gradient-to-br from-[#1C3A7A] via-[#243f7a] to-[#1a3570] rounded-3xl overflow-hidden shadow-2xl">
              {/* Confetti/Celebration Background */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {confettiPositions.map((pos, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-full"
                    style={{
                      left: pos.left,
                      top: pos.top,
                      backgroundColor: pos.color,
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: [0, 1, 0.5],
                      opacity: [0, 1, 0],
                      y: [0, -30, -60],
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.1,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="relative p-8 text-center">
                {/* Hearts animation */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="flex justify-center mb-4"
                >
                  <div className="relative">
                    <Heart className="w-12 h-12 text-[#FAB710] fill-[#FAB710]" />
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <Heart className="w-12 h-12 text-[#FAB710]/30 fill-[#FAB710]/30" />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold text-white mb-1"
                >
                  It&apos;s a Match!
                </motion.h2>

                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-white/70 text-sm mb-6"
                >
                  You and {matchedUser.name} liked each other
                </motion.p>

                {/* Profile Pictures */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex justify-center items-center mb-8"
                >
                  {/* Current User */}
                  <motion.div
                    initial={{ x: -50 }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.6, type: "spring" }}
                    className="relative"
                  >
                    <div className="w-28 h-28 rounded-full border-4 border-[#FAB710] overflow-hidden shadow-lg bg-white">
                      <img
                        src={currentUser?.photos?.[0] || `https://ui-avatars.com/api/?name=${currentUser?.name || 'You'}&size=200&background=1C3A7A&color=fff`}
                        alt="You"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#FAB710] rounded-full flex items-center justify-center shadow-md">
                      <Heart className="w-4 h-4 text-white fill-white" />
                    </div>
                  </motion.div>

                  {/* Heart connector */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8, type: "spring" }}
                    className="mx-[-12px] z-10"
                  >
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                    </div>
                  </motion.div>

                  {/* Matched User */}
                  <motion.div
                    initial={{ x: 50 }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.6, type: "spring" }}
                    className="relative"
                  >
                    <div className="w-28 h-28 rounded-full border-4 border-[#FAB710] overflow-hidden shadow-lg bg-white">
                      <img
                        src={matchedUser?.photos?.[0] || `https://ui-avatars.com/api/?name=${matchedUser?.name}&size=200&background=F7931E&color=fff`}
                        alt={matchedUser?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 bg-[#FAB710] rounded-full flex items-center justify-center shadow-md">
                      <Heart className="w-4 h-4 text-white fill-white" />
                    </div>
                  </motion.div>
                </motion.div>

                {/* Match Info */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="mb-6"
                >
                  <p className="text-white/60 text-xs">
                    Start a conversation and begin your recovery journey together
                  </p>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="flex flex-col gap-3"
                >
                  <Button
                    onClick={handleSendMessage}
                    className="w-full h-14 bg-[#FAB710] hover:bg-[#e8850f] text-white text-base font-semibold rounded-full shadow-lg flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Send a Message
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={handleKeepSwiping}
                    className="w-full h-12 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    Keep Swiping
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MatchModal;
